import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, Calculator, Hammer } from "lucide-react";

import { ProductShell, UpgradeNotice } from "@/components/app/product-shell";
import { useEntitlements } from "@/lib/billing/use-entitlements";

export const Route = createFileRoute("/_authenticated/app/reports")({
  head: () => ({
    meta: [
      { title: "Reports — AROS FLOW" },
      {
        name: "description",
        content:
          "One-page decision reports with the data source, comps, confidence, the assumptions you changed and the recommended next move.",
      },
      { property: "og:title", content: "Reports — AROS FLOW" },
      {
        property: "og:description",
        content: "Generate a one-page decision report from any analysis.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: ReportsPage,
});

const LINKS = [
  {
    to: "/app/analyze",
    icon: Calculator,
    title: "Deal report",
    body: "Run an analysis, then export the full underwriting PDF with your assumption snapshot and override history.",
  },
  {
    to: "/penthouse",
    icon: Hammer,
    title: "Repair and ARV reports",
    body: "The standalone repair and ARV estimators each export their own PDF with the same on-screen assumptions.",
  },
] as const;

function ReportsPage() {
  const ent = useEntitlements();

  if (!ent.loading && !ent.inPlan("pdf_reports")) {
    return (
      <ProductShell
        title="Reports"
        subtitle="One-page decision reports with sources, comps, confidence and your overrides."
      >
        <UpgradeNotice feature="Downloadable decision reports" plan="Starter" />
      </ProductShell>
    );
  }

  return (
    <ProductShell
      title="Reports"
      subtitle="Every report states where the numbers came from, how confident the read is, and what you changed by hand."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {LINKS.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="focus-key rounded-xl border border-[var(--glass-border)] bg-card p-5 hover:border-[var(--gold)]/50"
          >
            <l.icon className="h-5 w-5 text-gold" aria-hidden />
            <h2 className="mt-3 text-sm font-semibold">{l.title}</h2>
            <p className="mt-1.5 text-xs text-muted-foreground">{l.body}</p>
          </Link>
        ))}
      </div>

      <div className="mt-5 rounded-xl border border-[var(--glass-border)] bg-card p-5">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-gold" aria-hidden />
          <h2 className="text-sm font-semibold">What is on a decision report</h2>
        </div>
        <ul className="mt-3 grid gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
          {[
            "Property data source and the date it was last updated",
            "The comparable sales behind the value",
            "Confidence read on ARV, repairs and the offer",
            "Every assumption you edited, flagged as an override",
            "Need Score and the evidence behind it",
            "Recommended strategy and the offer range",
            "A plain-language reasoning summary",
            "The next action to take",
          ].map((i) => (
            <li key={i}>· {i}</li>
          ))}
        </ul>
      </div>
    </ProductShell>
  );
}
